import React, { useState, useEffect } from 'react';
import {
  Home, CreditCard, Building2, Zap, FileText, Settings, Bell,
  Users, Shield, MessageSquare, TrendingUp, ArrowLeft, ChevronRight,
  Phone, Download, CheckCircle, Mail, Crown, UserCog, Upload,
  BarChart3, AlertCircle, Activity
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useActingAsAdmin } from '@/hooks/useActingAsAdmin';
import { useAuth } from '@/contexts/AuthContext';
import { formatTSh } from '@/lib/currency';

type Role = 'user' | 'owner' | 'concierge' | 'admin';

const THEMES: Record<Role, {
  gradient: string; accent: string; accentBg: string; border: string;
  activeCard: string; btnActive: string; previewHeader: string; label: string;
  roleIcon: React.ElementType; ringColor: string;
}> = {
  user: {
    gradient: 'from-green-600 to-emerald-500', accent: 'text-green-600',
    accentBg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-400',
    activeCard: 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-green-100 dark:shadow-green-900/20',
    btnActive: 'bg-green-600 hover:bg-green-700 text-white',
    previewHeader: 'bg-green-600', label: 'Tenant Portal', roleIcon: Home,
    ringColor: 'ring-green-400',
  },
  owner: {
    gradient: 'from-blue-600 to-blue-500', accent: 'text-blue-600',
    accentBg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-400',
    activeCard: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-blue-100 dark:shadow-blue-900/20',
    btnActive: 'bg-blue-600 hover:bg-blue-700 text-white',
    previewHeader: 'bg-blue-600', label: 'Owner Portal', roleIcon: Crown,
    ringColor: 'ring-blue-400',
  },
  concierge: {
    gradient: 'from-teal-600 to-teal-500', accent: 'text-teal-600',
    accentBg: 'bg-teal-50 dark:bg-teal-900/20', border: 'border-teal-400',
    activeCard: 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 shadow-teal-100 dark:shadow-teal-900/20',
    btnActive: 'bg-teal-600 hover:bg-teal-700 text-white',
    previewHeader: 'bg-teal-600', label: 'Concierge Portal', roleIcon: Users,
    ringColor: 'ring-teal-400',
  },
  admin: {
    gradient: 'from-red-600 to-rose-500', accent: 'text-red-600',
    accentBg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-400',
    activeCard: 'border-red-500 bg-red-50 dark:bg-red-900/20 shadow-red-100 dark:shadow-red-900/20',
    btnActive: 'bg-red-600 hover:bg-red-700 text-white',
    previewHeader: 'bg-red-600', label: 'Admin Portal', roleIcon: Shield,
    ringColor: 'ring-red-400',
  },
};

// ─── Shared mini-components ───────────────────────────────────────────────────
const AmountBtn: React.FC<{ amount: number; btnActive: string }> = ({ amount, btnActive }) => {
  const [on, setOn] = useState(false);
  return (
    <button
      className={`py-1.5 rounded-lg text-[10px] font-semibold transition-all duration-150 ${on ? btnActive + ' scale-95' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:scale-105'}`}
      onClick={() => { setOn(true); setTimeout(() => setOn(false), 600); }}
    >{formatTSh(amount)}</button>
  );
};

const UnitBtn: React.FC<{ label: string; btnActive: string }> = ({ label, btnActive }) => {
  const [on, setOn] = useState(false);
  return (
    <button
      className={`py-2 rounded-lg text-[10px] font-semibold flex items-center gap-1 justify-center transition-all duration-150 ${on ? btnActive + ' scale-95' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:scale-105'}`}
      onClick={() => { setOn(true); setTimeout(() => setOn(false), 600); }}
    ><Building2 className="h-3 w-3" />{label}</button>
  );
};

const PdfBtn: React.FC<{ btnActive: string; onClick?: () => void }> = ({ btnActive, onClick }) => {
  const [on, setOn] = useState(false);
  return (
    <button
      className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-all duration-150 ${on ? btnActive : 'bg-gray-100 dark:bg-gray-700 text-gray-600'}`}
      onClick={() => { setOn(true); setTimeout(() => setOn(false), 700); onClick?.(); }}
    ><Download className="h-3 w-3" />{on ? '✓' : 'PDF'}</button>
  );
};

const CTA: React.FC<{ label: string; btnActive: string; onClick?: () => void }> = ({ label, btnActive, onClick }) => {
  const [on, setOn] = useState(false);
  return (
    <button
      className={`w-full py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${on ? btnActive + ' scale-95' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:scale-105'}`}
      onClick={() => { setOn(true); setTimeout(() => { setOn(false); onClick?.(); }, 700); }}
    >{on ? '✓ Opening…' : label}</button>
  );
};

const MiniHeader: React.FC<{ title: string; color: string }> = ({ title, color }) => (
  <div className={`${color} text-white px-3 py-2 flex items-center gap-2`}>
    <div className="w-2 h-2 rounded-full bg-white/60" />
    <span className="text-[11px] font-semibold truncate">{title}</span>
  </div>
);

const MiniCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-2.5 ${className}`}>{children}</div>
);

// ─── Step previews ────────────────────────────────────────────────────────────
type StepDef = { id: string; label: string; icon: React.ElementType; desc: string; preview: (t: typeof THEMES[Role], nav: (p: string) => void) => React.ReactNode };

const USER_STEPS: StepDef[] = [
  {
    id: 'topup', label: 'How to Top Up', icon: CreditCard, desc: 'Pay via mobile money or card in seconds',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Top Up" color={t.previewHeader} />
        <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-gray-50 dark:bg-gray-900">
          <p className="text-[10px] text-gray-500 font-semibold">① Pick an amount</p>
          <div className="grid grid-cols-3 gap-1.5">
            {[20000, 50000, 100000].map(a => <AmountBtn key={a} amount={a} btnActive={t.btnActive} />)}
          </div>
          <p className="text-[10px] text-gray-500 font-semibold">② Choose method</p>
          <MiniCard className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-green-500 flex-shrink-0" />
            <div><p className="text-[10px] font-semibold">Mobile Money</p><p className="text-[9px] text-gray-400">M-Pesa · Tigo · Airtel</p></div>
          </MiniCard>
          <p className="text-[10px] text-gray-500 font-semibold">③ Confirm</p>
          <CTA label="Pay Now" btnActive={t.btnActive} onClick={() => nav('/user/topup')} />
        </div>
      </div>
    ),
  },
  {
    id: 'balance', label: 'Check My Balance', icon: Home, desc: 'See your meter credit and usage chart',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Dashboard" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2.5 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <MiniCard>
            <p className="text-[9px] text-gray-400 mb-1">Current Balance</p>
            <p className="text-xl font-bold text-green-600">{formatTSh(45000)}</p>
            <p className="text-[10px] text-gray-400">≈ 112 kWh remaining</p>
          </MiniCard>
          <MiniCard>
            <p className="text-[9px] text-gray-400 mb-1.5">Usage this month</p>
            <div className="flex gap-0.5 items-end h-8">
              {[40,65,30,80,55,70,45,60,35,75,50,65].map((h,i) => (
                <div key={i} className="flex-1 bg-green-200 dark:bg-green-900 rounded-sm transition-all" style={{height:`${h}%`}} />
              ))}
            </div>
          </MiniCard>
          <CTA label="Open Dashboard" btnActive={t.btnActive} onClick={() => nav('/user/dashboard')} />
        </div>
      </div>
    ),
  },
  {
    id: 'receipts', label: 'Download Receipts', icon: Download, desc: 'Get PDF receipts for every payment',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Top-up History" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          {[{amount:100000,date:'08 Jun'},{amount:50000,date:'22 May'},{amount:150000,date:'03 May'}].map((r,i) => (
            <MiniCard key={i} className="flex items-center justify-between">
              <div><p className="text-[10px] font-semibold">{formatTSh(r.amount)}</p><p className="text-[9px] text-gray-400">{r.date}</p></div>
              <PdfBtn btnActive={t.btnActive} onClick={() => nav('/user/bills')} />
            </MiniCard>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'contracts', label: 'Sign My Contract', icon: FileText, desc: 'View and digitally sign your agreement',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Contracts" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2.5 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <MiniCard>
            <div className="flex items-start justify-between gap-2">
              <div><p className="text-[10px] font-semibold">GE-CON-0042</p><p className="text-[9px] text-gray-400">Apt 4B · Jan–Dec 2025</p></div>
              <Badge className="text-[9px] bg-amber-100 text-amber-700 border-0 shrink-0">Unsigned</Badge>
            </div>
          </MiniCard>
          <CTA label="Sign Contract" btnActive={t.btnActive} onClick={() => nav('/user/contracts')} />
        </div>
      </div>
    ),
  },
  {
    id: 'chatbot', label: 'Ask the AI', icon: MessageSquare, desc: 'Natural language answers about your usage',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="AI Chatbot" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <div className="flex justify-end"><div className="bg-green-100 text-green-800 text-[10px] rounded-xl rounded-br-sm px-2.5 py-1.5 max-w-[85%]">What's my usage this month?</div></div>
          <div className="flex justify-start"><div className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-[10px] rounded-xl rounded-bl-sm px-2.5 py-1.5 max-w-[85%] shadow-sm">You've used <strong>89 kWh</strong> — 12% less than last month 🎉</div></div>
          <CTA label="Open Chatbot" btnActive={t.btnActive} onClick={() => nav('/user/chatbot')} />
        </div>
      </div>
    ),
  },
  {
    id: 'consumption', label: 'View Consumption', icon: TrendingUp, desc: 'Interactive charts of your energy trends',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Consumption Evolution" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2.5 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <div className="flex gap-1.5">
            {['Daily','Weekly','Monthly'].map((l,i) => (
              <button key={i} className={`flex-1 text-[9px] py-1 rounded-md font-semibold ${i===0 ? t.btnActive : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>{l}</button>
            ))}
          </div>
          <MiniCard>
            <div className="flex gap-0.5 items-end h-16">
              {[30,55,40,70,45,85,60,50,75,40,65,80].map((h,i) => (
                <div key={i} className="flex-1 rounded-sm transition-all" style={{height:`${h}%`,backgroundColor: i===9?'#f59e0b':'#6ee7b7'}} />
              ))}
            </div>
            <p className="text-[9px] text-amber-600 mt-1">↑ Spike on day 10 — check appliances</p>
          </MiniCard>
          <CTA label="View Full Charts" btnActive={t.btnActive} onClick={() => nav('/user/consumption-evolution')} />
        </div>
      </div>
    ),
  },
];

const OWNER_STEPS: StepDef[] = [
  {
    id: 'topup', label: 'Top Up a Tenant', icon: Zap, desc: 'Pay directly for any of your units',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Top Up" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2.5 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <p className="text-[10px] text-gray-500 font-semibold">① Select unit</p>
          <div className="grid grid-cols-2 gap-1.5">{['Apt 2A','Apt 4B'].map(u => <UnitBtn key={u} label={u} btnActive={t.btnActive} />)}</div>
          <p className="text-[10px] text-gray-500 font-semibold">② Amount</p>
          <div className="grid grid-cols-3 gap-1.5">{[50000,100000,200000].map(a => <AmountBtn key={a} amount={a} btnActive={t.btnActive} />)}</div>
          <CTA label="Pay for Tenant" btnActive={t.btnActive} onClick={() => nav('/owner/topup')} />
        </div>
      </div>
    ),
  },
  {
    id: 'units', label: 'View My Units', icon: Building2, desc: 'Monitor all owned apartments and balances',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="My Units" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          {[{unit:'Apt 2A',tenant:'John D.',bal:85000,ok:true},{unit:'Apt 4B',tenant:'Sarah M.',bal:8000,ok:false}].map((u,i) => (
            <MiniCard key={i} className={`border-l-4 ${u.ok?'border-l-blue-400':'border-l-amber-400'}`}>
              <p className="text-[10px] font-semibold">{u.unit}</p>
              <p className="text-[9px] text-gray-400">{u.tenant}</p>
              <p className={`text-[10px] font-medium mt-0.5 ${u.ok?'text-blue-600':'text-amber-600'}`}>{formatTSh(u.bal)}</p>
            </MiniCard>
          ))}
          <CTA label="View All Units" btnActive={t.btnActive} onClick={() => nav('/owner/units')} />
        </div>
      </div>
    ),
  },
  {
    id: 'history', label: 'Top-up History', icon: CreditCard, desc: 'All transactions across your portfolio',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Top-up History" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          {[{amount:100000,unit:'Apt 2A',who:'Tenant',c:'border-l-blue-400'},{amount:50000,unit:'Apt 4B',who:'You',c:'border-l-purple-400'}].map((r,i) => (
            <MiniCard key={i} className={`border-l-4 ${r.c} flex items-center justify-between`}>
              <div><p className="text-[10px] font-semibold">{formatTSh(r.amount)}</p><p className="text-[9px] text-gray-400">{r.unit} · {r.who}</p></div>
              <PdfBtn btnActive={t.btnActive} onClick={() => nav('/owner/topup-history')} />
            </MiniCard>
          ))}
          <CTA label="Full History" btnActive={t.btnActive} onClick={() => nav('/owner/topup-history')} />
        </div>
      </div>
    ),
  },
  {
    id: 'concierge', label: 'Assign Concierge', icon: Users, desc: 'Delegate unit management to a concierge',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Assign Roles" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2.5 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <MiniCard>
            <p className="text-[10px] font-semibold mb-1">Unit: Apt 2A</p>
            <div className="flex items-center gap-2 p-1.5 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 text-[9px] font-bold">M</div>
              <span className="text-[10px]">Marie K.</span>
              <Badge className="ml-auto text-[9px] bg-teal-100 text-teal-700 border-0">Concierge</Badge>
            </div>
          </MiniCard>
          <CTA label="Assign Now" btnActive={t.btnActive} onClick={() => nav('/owner/assign-roles')} />
        </div>
      </div>
    ),
  },
];

const CONCIERGE_STEPS: StepDef[] = [
  {
    id: 'topup', label: 'Top Up an Apartment', icon: Zap, desc: 'Initiate payment on behalf of a tenant',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Top Up" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2.5 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <p className="text-[10px] text-gray-500 font-semibold">① Select apartment</p>
          <div className="grid grid-cols-2 gap-1.5">{['Apt 3C','Apt 7A'].map(u => <UnitBtn key={u} label={u} btnActive={t.btnActive} />)}</div>
          <p className="text-[10px] text-gray-500 font-semibold">② Amount</p>
          <div className="grid grid-cols-3 gap-1.5">{[20000,50000,100000].map(a => <AmountBtn key={a} amount={a} btnActive={t.btnActive} />)}</div>
          <CTA label="Initiate Payment" btnActive={t.btnActive} onClick={() => nav('/concierge/topup')} />
        </div>
      </div>
    ),
  },
  {
    id: 'apartments', label: 'View My Apartments', icon: Building2, desc: 'Monitor all apartments assigned to you',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="My Apartments" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          {[{u:'Apt 3C',ten:'Ahmed B.',bal:92000,ok:true},{u:'Apt 7A',ten:'Linda T.',bal:7000,ok:false}].map((a,i) => (
            <MiniCard key={i} className={`border-l-4 ${a.ok?'border-l-teal-400':'border-l-amber-400'}`}>
              <p className="text-[10px] font-semibold">{a.u}</p>
              <p className="text-[9px] text-gray-400">{a.ten}</p>
              <p className={`text-[10px] font-medium mt-0.5 ${a.ok?'text-teal-600':'text-amber-600'}`}>{formatTSh(a.bal)}</p>
            </MiniCard>
          ))}
          <CTA label="View All" btnActive={t.btnActive} onClick={() => nav('/concierge/apartments')} />
        </div>
      </div>
    ),
  },
  {
    id: 'history', label: 'Top-up History', icon: CreditCard, desc: 'Track all payments with color coding',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Top-up History" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <div className="flex gap-2 text-[9px] text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-teal-500 inline-block"/>Me</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500 inline-block"/>Tenant</span>
          </div>
          {[{amount:50000,u:'Apt 3C',who:'Me',c:'border-l-teal-400'},{amount:100000,u:'Apt 7A',who:'Tenant',c:'border-l-blue-400'}].map((r,i) => (
            <MiniCard key={i} className={`border-l-4 ${r.c} flex items-center justify-between`}>
              <div><p className="text-[10px] font-semibold">{formatTSh(r.amount)}</p><p className="text-[9px] text-gray-400">{r.u} · {r.who}</p></div>
              <PdfBtn btnActive={t.btnActive} onClick={() => nav('/concierge/topup-history')} />
            </MiniCard>
          ))}
          <CTA label="Full History" btnActive={t.btnActive} onClick={() => nav('/concierge/topup-history')} />
        </div>
      </div>
    ),
  },
];

const ADMIN_STEPS: StepDef[] = [
  {
    id: 'topup', label: 'Top Up a Tenant', icon: Zap, desc: 'Initiate payment for any tenant from Buildings',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Buildings — Top Up" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2.5 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <MiniCard><p className="text-[10px] font-semibold">Tower A · Apt 3B</p><p className="text-[9px] text-gray-400">Tenant: Ahmed B. · SM-0042</p></MiniCard>
          <div className="grid grid-cols-3 gap-1.5">{[50000,100000,200000].map(a => <AmountBtn key={a} amount={a} btnActive={t.btnActive} />)}</div>
          <CTA label="Initiate Top-up" btnActive={t.btnActive} onClick={() => nav('/admin/buildings')} />
        </div>
      </div>
    ),
  },
  {
    id: 'history', label: 'View All Top-ups', icon: CreditCard, desc: 'Color-coded org-wide transaction history',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Top-up History" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <div className="flex gap-2 text-[9px] text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500 inline-block"/>Tenant</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-teal-500 inline-block"/>Concierge</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500 inline-block"/>Admin</span>
          </div>
          {[{amount:100000,u:'Ahmed B.',c:'border-l-blue-400'},{amount:50000,u:'Marie K.',c:'border-l-teal-400'},{amount:200000,u:'John D.',c:'border-l-red-400'}].map((r,i) => (
            <MiniCard key={i} className={`border-l-4 ${r.c} flex items-center justify-between`}>
              <div><p className="text-[10px] font-semibold">{formatTSh(r.amount)}</p><p className="text-[9px] text-gray-400">{r.u}</p></div>
              <PdfBtn btnActive={t.btnActive} onClick={() => nav('/admin/topup-history')} />
            </MiniCard>
          ))}
          <CTA label="Full History" btnActive={t.btnActive} onClick={() => nav('/admin/topup-history')} />
        </div>
      </div>
    ),
  },
  {
    id: 'users', label: 'Manage Users', icon: Users, desc: 'Create, edit and deactivate user accounts',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="User Management" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          {['Ahmed B.','Sarah M.','John D.'].map((n,i) => (
            <MiniCard key={i} className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[9px] font-bold text-gray-600">{n[0]}</div>
              <span className="text-[10px] flex-1">{n}</span>
              <Badge className="text-[9px] bg-green-100 text-green-700 border-0">Active</Badge>
            </MiniCard>
          ))}
          <CTA label="Manage Users" btnActive={t.btnActive} onClick={() => nav('/admin/users')} />
        </div>
      </div>
    ),
  },
  {
    id: 'notify', label: 'Send Notification', icon: Bell, desc: 'Broadcast alerts to tenants or buildings',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Notifications" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2.5 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          <MiniCard><p className="text-[9px] text-gray-400 mb-1">Message</p><p className="text-[10px] text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded p-1.5">Maintenance on 15 Jun 10am–12pm. Plan ahead.</p></MiniCard>
          <MiniCard className="flex gap-2 items-center"><p className="text-[9px] text-gray-400">To:</p>{['All users','Tower A'].map((l,i) => <span key={i} className={`text-[9px] px-2 py-0.5 rounded-full ${i===0?'bg-red-100 text-red-700':'bg-gray-100 text-gray-600'}`}>{l}</span>)}</MiniCard>
          <CTA label="Send Now" btnActive={t.btnActive} onClick={() => nav('/admin/notifyc_client')} />
        </div>
      </div>
    ),
  },
  {
    id: 'contracts', label: 'Manage Contracts', icon: FileText, desc: 'Create and track tenant service agreements',
    preview: (t, nav) => (
      <div className="flex flex-col h-full">
        <MiniHeader title="Contracts" color={t.previewHeader} />
        <div className="flex-1 p-3 space-y-2 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
          {[{ref:'GE-CON-0042',u:'Ahmed B.',s:'Signed',c:'bg-green-100 text-green-700'},{ref:'GE-CON-0043',u:'Sarah M.',s:'Pending',c:'bg-amber-100 text-amber-700'}].map((c,i) => (
            <MiniCard key={i} className="flex items-center justify-between">
              <div><p className="text-[10px] font-semibold">{c.ref}</p><p className="text-[9px] text-gray-400">{c.u}</p></div>
              <Badge className={`text-[9px] border-0 ${c.c}`}>{c.s}</Badge>
            </MiniCard>
          ))}
          <CTA label="View Contracts" btnActive={t.btnActive} onClick={() => nav('/admin/contracts')} />
        </div>
      </div>
    ),
  },
];

const STEPS: Record<Role, StepDef[]> = { user: USER_STEPS, owner: OWNER_STEPS, concierge: CONCIERGE_STEPS, admin: ADMIN_STEPS };

// ─── FAQ Card ─────────────────────────────────────────────────────────────────
const FaqCard: React.FC<{
  step: StepDef; isActive: boolean; side: 'left' | 'right';
  theme: typeof THEMES[Role]; onClick: () => void;
}> = ({ step, isActive, side, theme, onClick }) => {
  const Icon = step.icon;
  return (
    <button
      onClick={onClick}
      className={`relative w-full text-left p-3 rounded-xl border-2 transition-all duration-200 group
        ${isActive
          ? `${theme.activeCard} shadow-lg scale-[1.02]`
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md hover:scale-[1.01]'
        }`}
    >
      {/* connector dot */}
      {isActive && (
        <div className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 shadow
          ${side === 'right' ? '-left-1.5' : '-right-1.5'}
          ${theme.border.replace('border-', 'bg-')}`}
        />
      )}
      <div className="flex items-start gap-2.5">
        <div className={`p-1.5 rounded-lg flex-shrink-0 ${isActive ? theme.accentBg : 'bg-gray-100 dark:bg-gray-800'}`}>
          <Icon className={`h-3.5 w-3.5 ${isActive ? theme.accent : 'text-gray-400'}`} />
        </div>
        <div className="min-w-0">
          <p className={`text-xs font-semibold leading-tight ${isActive ? theme.accent : 'text-gray-700 dark:text-gray-300'}`}>{step.label}</p>
          <p className="text-[10px] text-gray-400 mt-0.5 leading-snug">{step.desc}</p>
        </div>
      </div>
    </button>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const Documentation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const actingAsAdmin = useActingAsAdmin();
  const { isOwner, isConcierge } = useAuth();
  const navigate = useNavigate();

  const resolveRole = (): Role => {
    const p = searchParams.get('tab') as Role | null;
    if (p && ['user','owner','concierge','admin'].includes(p)) return p;
    if (actingAsAdmin) return 'admin';
    if (isOwner()) return 'owner';
    if (isConcierge()) return 'concierge';
    return 'user';
  };

  const [role, setRole] = useState<Role>(resolveRole);
  useEffect(() => { setRole(resolveRole()); setActive(0); }, [searchParams]);

  const theme = THEMES[role];
  const RoleIcon = theme.roleIcon;
  const steps = STEPS[role];
  const [active, setActive] = useState(0);

  // Split steps left/right around the phone
  const half = Math.ceil(steps.length / 2);
  const leftSteps = steps.slice(0, half);
  const rightSteps = steps.slice(half);

  const activeStep = steps[active];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
      {/* Header */}
      <div className={`bg-gradient-to-r ${theme.gradient} text-white px-4 py-3 flex items-center gap-3 flex-shrink-0`}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/')}
          className="text-white/80 hover:text-white hover:bg-white/15 gap-1.5 h-8 px-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <RoleIcon className="h-5 w-5" />
        <span className="font-semibold text-sm">{theme.label} — Interactive Guide</span>
        <Badge className="ml-auto bg-white/20 text-white border-white/30 text-xs">Click a card to explore</Badge>
      </div>

      {/* Body */}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8 overflow-auto">
        <div className="flex items-center gap-4 lg:gap-8 w-full max-w-5xl">

          {/* Left FAQ cards */}
          <div className="flex flex-col gap-3 w-48 lg:w-52 flex-shrink-0">
            {leftSteps.map((s, i) => (
              <FaqCard key={s.id} step={s} isActive={active === i} side="left"
                theme={theme} onClick={() => setActive(i)} />
            ))}
          </div>

          {/* Phone mockup */}
          <div className="flex-1 flex flex-col items-center gap-4 min-w-0">
            {/* Active label */}
            <div className="text-center">
              <h2 className={`text-lg font-bold ${theme.accent}`}>{activeStep.label}</h2>
              <p className="text-xs text-gray-400">Click the buttons inside the phone to interact</p>
            </div>

            {/* Phone */}
            <div className={`relative ring-4 ${theme.ringColor} ring-offset-2 ring-offset-gray-50 dark:ring-offset-gray-950 rounded-[2.8rem]`}>
              <div className="w-60 bg-gray-900 rounded-[2.8rem] p-2 shadow-2xl">
                <div className="w-14 h-3.5 bg-gray-800 rounded-full mx-auto mb-1 flex items-center justify-center">
                  <div className="w-8 h-1 bg-gray-700 rounded-full" />
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-[2.3rem] overflow-hidden h-[400px] flex flex-col">
                  {activeStep.preview(theme, navigate)}
                </div>
              </div>
              {/* Side buttons */}
              <div className="absolute right-0 top-20 w-1 h-10 bg-gray-700 rounded-r-sm" />
              <div className="absolute left-0 top-16 w-1 h-7 bg-gray-700 rounded-l-sm" />
              <div className="absolute left-0 top-26 w-1 h-7 bg-gray-700 rounded-l-sm" />
            </div>

            {/* Dots */}
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <button key={i} onClick={() => setActive(i)}
                  className={`rounded-full transition-all duration-200 ${i === active ? `w-5 h-2 ${theme.btnActive.split(' ')[0]}` : 'w-2 h-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'}`}
                />
              ))}
            </div>
          </div>

          {/* Right FAQ cards */}
          <div className="flex flex-col gap-3 w-48 lg:w-52 flex-shrink-0">
            {rightSteps.map((s, i) => {
              const globalIdx = half + i;
              return (
                <FaqCard key={s.id} step={s} isActive={active === globalIdx} side="right"
                  theme={theme} onClick={() => setActive(globalIdx)} />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
